/**
 * Unified input system for mouse + touch + keyboard + gamepad.
 * Detects: tap, swipe (up/down/left/right), and held ACTIONS.
 *
 * Pointer: pointerdown/pointerup on the canvas.
 * Keyboard: Arrow keys / WASD → swipe, Space → tap.
 * Gamepad: standard-mapping pads polled per frame (no events — the
 *          Gamepad API is poll-only by design).
 *
 * Game code subscribes to 'tap' and 'swipe' events and never needs
 * to know which device triggered them. Scenes that poll continuous
 * input (the platformer) call `isActionDown('jump' | 'left' |
 * 'right' | 'run' | 'up' | 'down')`, which unions the keyboard and
 * every connected gamepad — so keyboard and controller both Just
 * Work with zero per-scene device code.
 */

const SWIPE_THRESHOLD = 40;
const SWIPE_MAX_TIME = 400;
const TAP_MAX_MOVE = 15;
const TAP_MAX_TIME = 300;

/** Left-stick deflection beyond which it counts as a digital press. */
const GAMEPAD_DEADZONE = 0.35;

/**
 * Action → input bindings. `codes` are KeyboardEvent.code values;
 * `buttons` are standard-mapping gamepad button indices
 * (https://w3c.github.io/gamepad/#remapping): 0=A/Cross, 1=B/Circle,
 * 2=X/Square, 12-15=dpad up/down/left/right. `axis` reads the left
 * stick: [axisIndex, sign] presses when sign*value > deadzone.
 */
const ACTION_BINDINGS = {
    left:  { codes: ['ArrowLeft', 'KeyA'],           buttons: [14], axis: [0, -1] },
    right: { codes: ['ArrowRight', 'KeyD'],          buttons: [15], axis: [0, +1] },
    up:    { codes: ['ArrowUp', 'KeyW'],             buttons: [12], axis: [1, -1] },
    down:  { codes: ['ArrowDown', 'KeyS'],           buttons: [13], axis: [1, +1] },
    jump:  { codes: ['Space', 'KeyW', 'ArrowUp'],    buttons: [0, 1] },
    run:   { codes: ['ShiftLeft', 'ShiftRight'],     buttons: [2, 5] },
};

/** @typedef {'up'|'down'|'left'|'right'} SwipeDirection */

/**
 * Maps KeyboardEvent.code to a swipe direction.
 * @type {Record<string, SwipeDirection>}
 */
const KEY_DIRECTION_MAP = {
    ArrowUp:    'up',
    ArrowDown:  'down',
    ArrowLeft:  'left',
    ArrowRight: 'right',
    KeyW:       'up',
    KeyS:       'down',
    KeyA:       'left',
    KeyD:       'right',
};

const TAP_KEYS = new Set(['Space', 'Enter']);

/**
 * True when the event's target is a place the user is actively typing
 * into — a text-like <input>, a <textarea>, a <select>, or any
 * contenteditable element. Used to bail out of preventDefault-heavy
 * keyboard handling so modal filename prompts and other in-DOM forms
 * don't silently lose keystrokes (W/A/S/D, Space, Enter, arrows).
 *
 * Non-text input types (checkbox, button, range, …) still fall
 * through so e.g. Space on a focused checkbox in a future overlay
 * doesn't accidentally also fire a game tap, but typing into a
 * filename / score-name field always wins.
 */
function _isTextEditingTarget(target) {
    if (!target) return false;
    if (target.isContentEditable) return true;
    const tag = target.tagName?.toLowerCase?.();
    if (tag === 'textarea' || tag === 'select') return true;
    if (tag !== 'input') return false;
    const type = (target.type || 'text').toLowerCase();
    // Treat everything that accepts free-form text as "editing".
    const editableTypes = new Set([
        'text', 'search', 'email', 'url', 'tel', 'password',
        'number', 'date', 'datetime-local', 'month', 'time', 'week',
    ]);
    return editableTypes.has(type);
}

export class InputSystem {
    /** @type {HTMLElement} */
    _target = null;

    _pointerDown = false;
    _startX = 0;
    _startY = 0;
    _startTime = 0;

    /** Currently held keys (every KeyboardEvent.code, for polling). */
    keysDown = new Set();

    /** Latest per-frame gamepad snapshot: { buttons:Set<number>,
     *  axes:number[] } unioned across every connected pad, or null
     *  when no pad is connected. Refreshed by `pollGamepads()`. */
    _padState = null;
    /** True once any pad has connected (skips polling entirely until
     *  then — `getGamepads()` allocates on some browsers). */
    _padSeen = false;
    _boundPadConnect = null;

    /** Registered listeners */
    _listeners = {
        tap: [],
        swipe: [],
    };

    _boundDown = null;
    _boundUp = null;
    _boundKeyDown = null;
    _boundKeyUp = null;

    /**
     * @param {HTMLElement} target — the canvas element
     */
    init(target) {
        this._target = target;

        // ── pointer ──
        this._boundDown = (e) => this._onPointerDown(e);
        this._boundUp = (e) => this._onPointerUp(e);

        target.addEventListener('pointerdown', this._boundDown, { passive: true });
        target.addEventListener('pointerup', this._boundUp, { passive: true });
        target.addEventListener('pointercancel', this._boundUp, { passive: true });

        // ── keyboard ──
        this._boundKeyDown = (e) => this._onKeyDown(e);
        this._boundKeyUp = (e) => this._onKeyUp(e);

        window.addEventListener('keydown', this._boundKeyDown);
        window.addEventListener('keyup', this._boundKeyUp);

        // ── gamepad ──
        // The Gamepad API is poll-only; the connect event just arms
        // polling so scenes without a pad never pay for getGamepads().
        this._boundPadConnect = () => { this._padSeen = true; };
        window.addEventListener('gamepadconnected', this._boundPadConnect);
    }

    // ── gamepad + action layer ─────────────────────────

    /**
     * Snapshot every connected standard-mapping gamepad into one
     * unioned digital state. Call ONCE per frame (the platformer does
     * it at the top of its input read); `isActionDown` then reads the
     * cached snapshot so per-action queries stay O(1).
     */
    pollGamepads() {
        if (!this._padSeen || typeof navigator.getGamepads !== 'function') {
            this._padState = null;
            return;
        }
        let state = null;
        for (const pad of navigator.getGamepads()) {
            if (!pad || !pad.connected) continue;
            if (!state) state = { buttons: new Set(), axes: [0, 0] };
            pad.buttons.forEach((b, i) => {
                if (b && (b.pressed || b.value > 0.5)) state.buttons.add(i);
            });
            // Union sticks by keeping the strongest deflection per axis.
            for (let a = 0; a < 2; a++) {
                const v = pad.axes?.[a] || 0;
                if (Math.abs(v) > Math.abs(state.axes[a])) state.axes[a] = v;
            }
        }
        this._padState = state;
    }

    /**
     * True while the named action is held on ANY device — keyboard
     * keys and every connected gamepad are checked together.
     *
     * @param {'left'|'right'|'up'|'down'|'jump'|'run'} action
     */
    isActionDown(action) {
        const b = ACTION_BINDINGS[action];
        if (!b) return false;
        for (const code of b.codes) {
            if (this.keysDown.has(code)) return true;
        }
        const pad = this._padState;
        if (pad) {
            if (b.buttons) {
                for (const idx of b.buttons) {
                    if (pad.buttons.has(idx)) return true;
                }
            }
            if (b.axis) {
                const [axisIdx, sign] = b.axis;
                if ((pad.axes[axisIdx] || 0) * sign > GAMEPAD_DEADZONE) return true;
            }
        }
        return false;
    }

    /** True when any pad is currently connected (UI hints, etc.). */
    hasGamepad() {
        return !!this._padState;
    }

    /**
     * @param {'tap'|'swipe'} event
     * @param {Function} callback
     */
    on(event, callback) {
        if (this._listeners[event]) {
            this._listeners[event].push(callback);
        }
    }

    /**
     * @param {'tap'|'swipe'} event
     * @param {Function} callback
     */
    off(event, callback) {
        const list = this._listeners[event];
        if (!list) return;
        const idx = list.indexOf(callback);
        if (idx !== -1) list.splice(idx, 1);
    }

    destroy() {
        if (this._target) {
            this._target.removeEventListener('pointerdown', this._boundDown);
            this._target.removeEventListener('pointerup', this._boundUp);
            this._target.removeEventListener('pointercancel', this._boundUp);
        }
        window.removeEventListener('keydown', this._boundKeyDown);
        window.removeEventListener('keyup', this._boundKeyUp);
        window.removeEventListener('gamepadconnected', this._boundPadConnect);

        this._listeners.tap.length = 0;
        this._listeners.swipe.length = 0;
        this.keysDown.clear();
        this._padState = null;
    }

    // ── pointer handling ───────────────────────────────

    /** @param {PointerEvent} e */
    _onPointerDown(e) {
        this._pointerDown = true;
        this._startX = e.clientX;
        this._startY = e.clientY;
        this._startTime = performance.now();
    }

    /** @param {PointerEvent} e */
    _onPointerUp(e) {
        if (!this._pointerDown) return;
        this._pointerDown = false;

        const dx = e.clientX - this._startX;
        const dy = e.clientY - this._startY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const elapsed = performance.now() - this._startTime;

        if (dist < TAP_MAX_MOVE && elapsed < TAP_MAX_TIME) {
            this._emit('tap', { x: e.clientX, y: e.clientY });
            return;
        }

        if (dist >= SWIPE_THRESHOLD && elapsed < SWIPE_MAX_TIME) {
            const direction = this._resolveSwipeDirection(dx, dy);
            this._emit('swipe', { direction, dx, dy, x: e.clientX, y: e.clientY });
        }
    }

    // ── keyboard handling ──────────────────────────────

    /** @param {KeyboardEvent} e */
    _onKeyDown(e) {
        if (e.repeat) return;

        // Don't hijack keys while the user is typing into a form field
        // (text input, textarea, select, or any contenteditable region).
        // Without this guard the WASD movement bindings call
        // preventDefault() on every W/A/S/D keystroke even when the
        // active focus is a modal filename input — producing the
        // classic "I can't type my name" bug where letters silently
        // vanish from the input. Same applies to Space/Enter, which
        // are TAP_KEYS but also the primary way to insert a space or
        // submit a form.
        if (_isTextEditingTarget(e.target)) return;

        // Track EVERY held key so the action layer (and any polling
        // scene) can read modifiers like Shift and Space — previously
        // only direction keys landed here, which silently broke
        // `keysDown.has('Space')`-style polling.
        this.keysDown.add(e.code);

        const direction = KEY_DIRECTION_MAP[e.code];
        if (direction) {
            e.preventDefault();
            this._emit('swipe', { direction, dx: 0, dy: 0, x: 0, y: 0 });
            return;
        }

        if (TAP_KEYS.has(e.code)) {
            e.preventDefault();
            this._emit('tap', { x: 0, y: 0 });
        }
    }

    /** @param {KeyboardEvent} e */
    _onKeyUp(e) {
        // Mirror the keydown guard: if the user was typing in a form
        // field we never recorded the keydown, so don't try to clear
        // it on keyup either. Prevents stale entries in keysDown if a
        // focus flip happens mid-press.
        if (_isTextEditingTarget(e.target)) return;
        this.keysDown.delete(e.code);
    }

    // ── shared ─────────────────────────────────────────

    /**
     * @param {number} dx
     * @param {number} dy
     * @returns {SwipeDirection}
     */
    _resolveSwipeDirection(dx, dy) {
        if (Math.abs(dx) > Math.abs(dy)) {
            return dx > 0 ? 'right' : 'left';
        }
        return dy > 0 ? 'down' : 'up';
    }

    /**
     * @param {'tap'|'swipe'} event
     * @param {Object} data
     */
    _emit(event, data) {
        const list = this._listeners[event];
        for (let i = 0; i < list.length; i++) {
            list[i](data);
        }
    }
}
